import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Plus } from "lucide-react";
import { format } from "date-fns";
import {
  useCustomerCreditsAdmin,
  useCustomerCreditsSummary,
  type CustomerCredit,
} from "@/api/customer-credits";
import { IssueVoucherDialog } from "./issue-voucher-dialog";

interface CustomerVouchersDialogProps {
  customerId: string;
  customerName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * #262 — admin view of a customer's voucher wallet.
 *
 * Lists every voucher (open, used, expired). Admin sees more than the
 * customer (who sees only non-expired). "Issue voucher" opens the
 * IssueVoucherDialog as a stacked modal.
 */
export function CustomerVouchersDialog({
  customerId,
  customerName,
  open,
  onOpenChange,
}: CustomerVouchersDialogProps) {
  const [issueOpen, setIssueOpen] = useState(false);
  const { data: credits, isLoading } = useCustomerCreditsAdmin(customerId);
  const { data: summary } = useCustomerCreditsSummary(customerId);

  const summaryLine = summary
    ? [
        summary.amountCents > 0
          ? `$${(summary.amountCents / 100).toFixed(2)} amount credit`
          : null,
        summary.freeLineItemCount.PICKUP > 0
          ? `${summary.freeLineItemCount.PICKUP} free pickup${summary.freeLineItemCount.PICKUP === 1 ? "" : "s"}`
          : null,
        summary.freeLineItemCount.DELIVERY > 0
          ? `${summary.freeLineItemCount.DELIVERY} free deliver${summary.freeLineItemCount.DELIVERY === 1 ? "y" : "ies"}`
          : null,
      ]
        .filter(Boolean)
        .join(" · ") || "No outstanding vouchers"
    : "Loading…";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Vouchers — {customerName}</DialogTitle>
            <DialogDescription>{summaryLine}</DialogDescription>
          </DialogHeader>

          <div className="flex justify-end mb-2">
            <Button size="sm" onClick={() => setIssueOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Issue voucher
            </Button>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : !credits || credits.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-8">
              No vouchers issued for this customer.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Amount / Scope</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Used on</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {credits.map((c) => (
                  <CreditRow key={c.id} credit={c} />
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      <IssueVoucherDialog
        customerId={customerId}
        customerName={customerName}
        open={issueOpen}
        onOpenChange={setIssueOpen}
      />
    </>
  );
}

function CreditRow({ credit }: { credit: CustomerCredit }) {
  const now = new Date();
  const isExpired =
    credit.redeemedAt == null &&
    credit.validUntil != null &&
    new Date(credit.validUntil) < now;
  const isRedeemed = credit.redeemedAt != null;
  const status: "open" | "used" | "expired" = isRedeemed
    ? "used"
    : isExpired
      ? "expired"
      : "open";

  const statusColors: Record<typeof status, string> = {
    open: "bg-emerald-100 text-emerald-900",
    used: "bg-gray-100 text-gray-700",
    expired: "bg-amber-100 text-amber-900",
  };

  const amountOrScope =
    credit.kind === "AMOUNT"
      ? `$${((credit.amountCents ?? 0) / 100).toFixed(2)}`
      : (credit.scope ?? "—");

  return (
    <TableRow>
      <TableCell>
        <Badge variant="outline" className={statusColors[status]}>
          {status === "open" ? "Open" : status === "used" ? "Used" : "Expired"}
        </Badge>
      </TableCell>
      <TableCell className="text-xs font-mono">{credit.kind}</TableCell>
      <TableCell className="text-sm">{amountOrScope}</TableCell>
      <TableCell className="text-xs font-mono">{credit.sourceKind}</TableCell>
      <TableCell
        className="text-xs max-w-[280px] truncate"
        title={credit.reason}
      >
        {credit.reason}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {credit.validUntil
          ? format(new Date(credit.validUntil), "MMM d, yyyy")
          : "—"}
      </TableCell>
      <TableCell className="text-xs">
        {credit.redeemedOrder?.orderNumber
          ? `#${credit.redeemedOrder.orderNumber}`
          : "—"}
      </TableCell>
    </TableRow>
  );
}
